import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import ListLayout from "../../Components/List";
import ModelFormModal from "./ModelFormModal";
import ProfileAvatar from "../../Assets/images/avatar.png";
import useListParams from "../../Helpers/useListParams";

const INITIAL_FILTERS = { search: "", searchKey: "modelName", count: 10, brandId: "", segment: "", status: "", sortBy: "createdAt", sort: false };

const SEARCH_KEYS = [{ key: "modelName", value: "Model name" }];

const ModelList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [brands, setBrands] = useState([]);
    const [segments, setSegments] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);

    useEffect(() => {
        (async () => {
            const [brandResponse, segmentResponse] = await Promise.all([
                Handler({ method: "GET", url: "/admin/vehicle/brand/active" }),
                Handler({ method: "GET", url: "/admin/business/site/segment/view" }),
            ]);
            setBrands(brandResponse?.success ? brandResponse?.data?.data || [] : []);
            setSegments(segmentResponse?.success ? Object.keys(segmentResponse?.data?.data || {}) : []);
        })();
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/admin/vehicle/model/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.brandId ? { brandId: filters.brandId } : {}),
                ...(filters.segment ? { segment: filters.segment } : {}),
                ...(filters.status !== "" ? { status: filters.status } : {}),
                sortBy: filters.sortBy,
                sort: filters.sort,
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.brandId, filters.segment, filters.status, filters.sortBy, filters.sort]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const filterItems = useMemo(() => ({
        dropdown: [
            {
                key: "brandId",
                name: "Brand",
                options: [{ key: "", name: "All" }, ...brands.map((b) => ({ key: b.brandId, name: b.brand }))],
            },
            {
                key: "segment",
                name: "Segment",
                options: [{ key: "", name: "All" }, ...segments.map((s) => ({ key: s, name: s }))],
            },
            {
                key: "status",
                name: "Status",
                options: [{ key: "", name: "All" }, { key: true, name: "Active" }, { key: false, name: "Inactive" }],
            },
        ],
        sort: [
            { key: "modelName", name: "Name" },
            { key: "createdAt", name: "Created" },
        ],
    }), [brands, segments]);

    const columns = [
        {
            key: "logo",
            name: "Logo",
            mandatory: true,
            render: (row) => (
                <div className="d-flex">
                    <img
                        src={row.logo || ProfileAvatar}
                        alt={row.modelName}
                        className="rounded-circle"
                        width={40}
                        height={40}
                        style={{ objectFit: "cover" }}
                    />
                </div>
            ),
        },
        {
            key: "modelName",
            name: "Model",
            mandatory: true,
            render: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex flex-column">
                        <span className="fw-semibold text-dark text-capitalize">{row.modelName || ""}</span>
                    </div>
                </div>
            ),
        },
        {
            key: "brand",
            name: "Brand",
            render: (row) => (
                <div className="small">
                    <div className="text-dark text-capitalize">{row.brand || ""}</div>
                </div>
            ),
        },
        {
            key: "segment",
            name: "Segment",
            render: (row) => (
                <div className="small">
                    <div className="text-dark text-capitalize">{row.segment || ""}</div>
                </div>
            ),
        },
        {
            key: "subSegment",
            name: "Sub Segment",
            render: (row) => (
                <div className="small">
                    <div className="text-dark text-capitalize">{row.subSegment || ""}</div>
                </div>
            ),
        },
        {
            key: "seats",
            name: "Seats",
            render: (row) => (
                <div className="small">
                    <div className="text-dark">{row.seats ?? ""}</div>
                </div>
            ),
        },
        {
            key: "status",
            name: "Status",
            render: (row) => {
                const isActive = row.status === true;
                return (
                    <span className={`badge rounded-pill ${isActive ? "text-bg-success" : "text-bg-danger"}`}>
                        <i className={`bi ${isActive ? "bi-check-circle" : "bi-x-circle"}`}></i>
                        {isActive ? "Active" : "Inactive"}
                    </span>
                );
            },
        },
        {
            key: "siteEnabled",
            name: "Customer Site",
            render: (row) => {
                // A model saved before the switch existed counts as on, the same rule the customer site uses.
                const shown = row.siteData?.enabled !== false;
                return (
                    <span className={`badge rounded-pill ${shown ? "text-bg-success" : "text-bg-secondary"}`} title={row.seo_name ? `/models/${row.seo_name}` : undefined}>
                        {shown ? "On" : "Off"}
                    </span>
                );
            },
        },
        {
            key: "createdAt",
            name: "Created",
            render: (row) => (
                <div className="small">
                    <div className="text-dark">{row.createdAt ? formatDateTime(row.createdAt) : ""}</div>
                </div>
            ),
        },
    ];

    return (
        <>
            <ListLayout
                title="Models"
                description="Vehicle models across every brand - the middle of the Brand -> Model -> Variant catalog."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by model name..."
                onRowClick={(row) => navigate(`/model/${row.modelId}`)}
                addList={() => setShowAddModal(true)}
            />

            {showAddModal ? (
                <ModelFormModal
                    mode="create"
                    brandType={"cars"}
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default ModelList;
